import posthog from 'posthog-js'
import { useParams } from 'react-router-dom'
import { FiChevronLeft } from 'react-icons/fi'
import { hasHashtag } from 'components/hashtagText/hashtags'
import { MoneyPoolTransactionTypes } from 'utilities/helper/moneyPools'
import { timeOfDay } from '../days'
import { DirectionTile, SignedAmount, StatusChip } from '../parts'
import {
    getTransactionStatusText,
    isCashin,
} from '../../../../../utilities/helper/Funds'

interface IItemProps {
    item: IMoneyPoolTransactionResult
    isManager: boolean
    /** the list owns the one details sheet; a row only asks for it */
    onOpen: () => void
}

/**
 * Cash-ins and cash-outs are two tables merged into one list, so a bare `id`
 * can turn up twice. Anything that tells rows apart needs the kind as well.
 */
export function transactionKey(item: IMoneyPoolTransactionResult) {
    return `${isCashin(item) ? 'in' : 'out'}-${item.id}`
}

export default function Item({ item, isManager, onOpen }: IItemProps) {
    const { id } = useParams()
    const status = getTransactionStatusText(item)
    const cashin = isCashin(item)
    const time = timeOfDay(item.jalali_time)

    const openDetails = () => {
        // The description is where hashtags live, so whether one is present is
        // the only way to tell a filing manager apart from someone checking an
        // amount. The text itself is deliberately not sent.
        posthog.capture('transaction_detail_opened', {
            moneypool_id: Number(id),
            is_cashin: cashin,
            has_description: !!item.tag,
            has_hashtag: hasHashtag(item.tag ?? ''),
            is_manager: isManager,
        })
        onOpen()
    }

    return (
        <>
            {/* One line of a ledger, read right to left the way it is said
                aloud: who, what for — then how much and when. The whole row
                opens the details; the day it belongs to is the heading above
                it, so only the time is repeated here. */}
            <button
                type="button"
                onClick={openDetails}
                className="flex w-full items-start gap-2.5 px-3 py-3 text-right transition-colors active:bg-sunken focus-visible:bg-sunken focus-visible:outline-none"
            >
                <span className="mt-0.5">
                    <DirectionTile cashin={cashin} />
                </span>

                <span className="min-w-0 flex-1">
                    <span className="block break-words text-[15px] font-bold leading-6 text-black">
                        {item.poolship_name}
                    </span>
                    {/* The chip rides on the type's line and only drops to its
                        own when the two will not fit — most rows stay two
                        lines tall. */}
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-[13px] leading-5 text-text-body">
                            {MoneyPoolTransactionTypes[item.type]}
                        </span>
                        <StatusChip status={status} />
                    </span>
                    {item.tag ? (
                        <span className="mt-1 block truncate text-xs leading-5 text-text-secondary">
                            {item.tag}
                        </span>
                    ) : null}
                </span>

                <span className="flex shrink-0 flex-col items-end">
                    <SignedAmount amount={item.amount} cashin={cashin} />
                    {/* The chevron sits down here, beside the time, so it
                        never competes with a long name for the first line. */}
                    <span className="mt-0.5 flex items-center gap-0.5 text-xs leading-5 text-text-secondary">
                        {time}
                        <FiChevronLeft
                            aria-hidden
                            className="-ml-1 h-3.5 w-3.5 text-text-muted"
                        />
                    </span>
                </span>
            </button>
        </>
    )
}
